import { Alert, Box, Button, TextField, Toolbar, Typography } from '@mui/material';
import FileDownloadOutlineIcon from 'mdi-react/FileDownloadOutlineIcon';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import authToken from 'store/selectors/authToken';
import isSuperAdmin from 'store/selectors/isSuperAdmin';
import {
  downloadSuperadminRequestEvidenceBundle,
  getSuperadminEvidenceExportErrorMessage,
} from 'services/ommniosign/superadminEvidenceExport';
import { I18n } from 'utils/i18n';
import EvidenceExportPasswordDialog from './EvidenceExportPasswordDialog';

const REQUEST_ID_PATTERN = /^SR-[0-9]+-[A-Z0-9]+$/;

const AdminEvidenceExportScene = () => {
  const token = useSelector(authToken);
  const isAdmin = useSelector(isSuperAdmin);
  const [requestId, setRequestId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [exportPassword, setExportPassword] = useState(null);
  const [exportedFilename, setExportedFilename] = useState('');

  const resetPasswordState = useCallback(() => {
    setExportPassword(null);
    setExportedFilename('');
  }, []);

  const handleDownload = useCallback(async () => {
    const trimmed = requestId.trim();
    if (!REQUEST_ID_PATTERN.test(trimmed)) {
      setError(I18n.t('AdminEvidenceExportScene.InvalidRequestId'));
      return;
    }
    if (!token?.hash) {
      setError(I18n.t('AdminEvidenceExportScene.NoSession'));
      return;
    }
    setError(null);
    setLoading(true);
    resetPasswordState();
    try {
      const { filename, password } = await downloadSuperadminRequestEvidenceBundle(
        trimmed,
        token.hash,
      );
      setExportedFilename(filename);
      setExportPassword(password);
      setPasswordDialogOpen(true);
    } catch (e) {
      setError(getSuperadminEvidenceExportErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [requestId, token, resetPasswordState]);

  const handleClosePasswordDialog = useCallback(() => {
    setPasswordDialogOpen(false);
    resetPasswordState();
  }, [resetPasswordState]);

  if (!isAdmin) {
    return (
      <Box
        sx={{
          p: 3,
        }}
      >
        <Alert severity="warning">{I18n.t('AdminEvidenceExportScene.Forbidden')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Toolbar disableGutters sx={{ mb: 2 }}>
        <FileDownloadOutlineIcon size={32} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        <Typography variant="h4" component="h1">
          {I18n.t('AdminEvidenceExportScene.Title')}
        </Typography>
      </Toolbar>

      <Typography
        variant="body1"
        paragraph
        sx={{
          color: 'text.secondary',
        }}
      >
        {I18n.t('AdminEvidenceExportScene.Description')}
      </Typography>

      <Typography
        variant="body2"
        paragraph
        sx={{
          color: 'text.secondary',
        }}
      >
        {I18n.t('AdminEvidenceExportScene.HintFromChat')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label={I18n.t('AdminEvidenceExportScene.RequestIdLabel')}
        placeholder="SR-..."
        value={requestId}
        onChange={e => setRequestId(e.target.value)}
        margin="normal"
        disabled={loading}
      />

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleDownload} disabled={loading}>
          {loading
            ? I18n.t('AdminEvidenceExportScene.Exporting')
            : I18n.t('AdminEvidenceExportScene.Export')}
        </Button>
      </Box>

      <EvidenceExportPasswordDialog
        open={passwordDialogOpen}
        onClose={handleClosePasswordDialog}
        password={exportPassword}
        filename={exportedFilename}
      />
    </Box>
  );
};

export default AdminEvidenceExportScene;
